import React, { useState } from 'react';
import axios from 'axios';

import CardDialog from '~/components/CardDialog';
import AlertBanner from '~/components/core/AlertBanner';
import Grid from '~/components/core/Atomic/Grid/Grid';
import CancelButton from '~/components/core/Buttons/CancelButton';
import { PERMISSION_ACTIONS, PERMISSION_VERBS } from '~/components/core/Permissions/PermissionUtils';
import { RestrictedPermissions } from '~/components/core/Permissions/RestrictedPermissions';
import Text from '~/components/core/TextComponents/Text';
import { useLobConfiguredFields } from '~/components/hooks/useLobConfiguredFields';
import type { CoveredObjectForFe } from '~/server_shared/swagger/data-contracts';
import { isClaimPolicyManuallyFilled, reportAxiosError } from '~/Utils';
import type { PopulatedClaim } from '~/Utils/populateClaim';

import EditCoveredEntityDialog from '../EditCoveredEntityDialog/EditCoveredEntityDialog';

// A claim-owned object has no policy, so its covered entity can't be fetched from
// /policies/{id}/covered_entities/{id} the way CoveredEntityCard does — and it is born
// with the monetary terms unset anyway. Every key has to be present: the covered-object
// PATCH reads each one straight out of the `covered_entity` it is sent.
const UNSET_COVERED_ENTITY = {
  deductible_per_policy_per_covered_entity: null,
  limit_per_policy_per_covered_entity: null,
  limit_per_covered_entity: null,
};

interface UnlistedCoveredObjectCardProps {
  claim: PopulatedClaim;
  coveredObject: CoveredObjectForFe;
  onClaimUpdate: () => void;
}

const UnlistedCoveredObjectCard: React.FC<UnlistedCoveredObjectCardProps> = ({
  claim,
  coveredObject,
  onClaimUpdate,
}) => {
  const [showEditCoveredEntityDialog, setShowEditCoveredEntityDialog] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  // @ts-expect-error TODO: widen useLobConfiguredFields' TS signature to accept the runtime covered object (and migrate the file to TS).
  const { Icon, coveredObjectTypeLabel, configuredFields, defaultFields } = useLobConfiguredFields(coveredObject, true);
  const isManualPolicy = isClaimPolicyManuallyFilled(claim);
  const policyPrefix = isManualPolicy ? '' : 'api_';
  const typeLabel = coveredObjectTypeLabel || 'Covered Object';
  const displayName = coveredObject.display_name || coveredObject.contact?.full_name || typeLabel;

  const handleAddToPolicy = async (values: Record<string, unknown>) => {
    setIsSaving(true);
    try {
      const url = `/api/v1/claims/${claim.id}/${policyPrefix}policy/covered_objects/${coveredObject.id}`;
      await axios.patch(url, values);
      await axios.patch(`${url}/set_listed`);
      await onClaimUpdate();
      setShowEditCoveredEntityDialog(false);
    } catch (error) {
      reportAxiosError(error);
    }
    setIsSaving(false);
  };

  return (
    <CardDialog
      headerStyle={{ padding: '0 0 24px' }}
      containerStyle={{ padding: '24px', marginBottom: '24px' }}
      contentStyle={{ padding: '0' }}
      title="Unlisted First Party"
    >
      <AlertBanner
        title="Please Note"
        alertType={AlertBanner.ALERT_TYPES.INFO}
        note={`There is an unlisted first party ${typeLabel.toLowerCase()}. Please check if the policy covers it.`}
        withIcon
      />
      <RestrictedPermissions
        action={isManualPolicy ? PERMISSION_ACTIONS.MANUAL_POLICY : PERMISSION_ACTIONS.API_POLICY}
        verb={PERMISSION_VERBS.WRITE}
      >
        <div className="mb-4 mt-20 w-full rounded-md border border-solid border-slate-600 p-24">
          <Grid container spacing={2} justify="space-between" alignItems="center" wrap="nowrap">
            <Grid item>
              <div className="flex items-center gap-16">
                {Icon ? <Icon /> : null}
                <Text variant={Text.VARIANTS.LG} weight={Text.WEIGHTS.SEMI_BOLD}>
                  {displayName}
                </Text>
              </div>
            </Grid>
            <Grid item>
              <CancelButton content="ADD TO POLICY" onClick={() => setShowEditCoveredEntityDialog(true)} />
            </Grid>
          </Grid>
        </div>
      </RestrictedPermissions>
      {showEditCoveredEntityDialog ? (
        <EditCoveredEntityDialog
          icon={Icon ? <Icon /> : undefined}
          coveredObjectTypeLabel={`Add ${typeLabel} to policy`}
          defaultFields={defaultFields}
          configuredFields={configuredFields}
          coveredEntity={UNSET_COVERED_ENTITY}
          onSaveClick={handleAddToPolicy}
          onClose={() => !isSaving && setShowEditCoveredEntityDialog(false)}
          isSaving={isSaving}
          titleWithoutPrefix
        />
      ) : null}
    </CardDialog>
  );
};

export default UnlistedCoveredObjectCard;
